// src/components/dashboard/DashboardCharts.js
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// Activity Chart Component
export const ActivityChart = ({ data = [] }) => {
  // Transform the data to match what the chart expects
  const transformedData = data.map(item => ({
    name: item.name,
    login: item.login || 0,
    create: item.create || 0,
    update: item.update || 0
  }));

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-300">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Activité Récente</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={transformedData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" stroke="#666" />
          <YAxis stroke="#666" />
          <Tooltip 
            contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}
          />
          <Legend />
          <Bar dataKey="login" fill="#8884d8" name="Connexions" radius={[4, 4, 0, 0]} />
          <Bar dataKey="create" fill="#82ca9d" name="Créations" radius={[4, 4, 0, 0]} />
          <Bar dataKey="update" fill="#ffc658" name="Mises à jour" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// User Distribution Chart Component
export const UserDistributionChart = ({ data = [] }) => {
  // Filter out entries with zero values
  const filteredData = data.filter(entry => entry.value > 0);
  
  // Calculate total for percentage calculations
  const total = filteredData.reduce((sum, entry) => sum + entry.value, 0);

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-300">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Répartition des Utilisateurs</h3>
      <div className="flex">
        <div className="w-2/3">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={filteredData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {filteredData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [`${value} (${((value/total)*100).toFixed(1)}%)`, name]}
                contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}
              />
              <Legend 
                layout="vertical" 
                verticalAlign="middle" 
                align="right"
                formatter={(value, entry, index) => (
                  <span className="text-sm">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-1/3 flex flex-col justify-center">
          {filteredData.map((entry, index) => (
            <div key={index} className="flex items-center mb-2">
              <div 
                className="w-4 h-4 rounded-full mr-2" 
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              ></div>
              <span className="text-sm font-medium">{entry.name}: {entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Company Status Chart Component
export const CompanyStatusChart = ({ data = [] }) => {
  // Transform the data to match what the chart expects
  const transformedData = data.map(item => ({
    name: item.name,
    count: item.count || 0
  }));

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-300">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Statut des Entreprises</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={transformedData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis type="number" stroke="#666" />
          <YAxis dataKey="name" type="category" scale="band" stroke="#666" />
          <Tooltip 
            formatter={(value) => [value, 'Nombre']}
            labelFormatter={(value) => `Statut: ${value}`}
            contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}
          />
          <Bar dataKey="count" name="Nombre d'entreprises" radius={[0, 0, 4, 4]}>
            {transformedData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.name === 'Actives' ? '#10B981' : '#EF4444'} 
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Metrics Card Component
export const MetricsCard = ({ 
  title, 
  value, 
  previousValue,
  icon, 
  trend, 
  color = 'blue',
  linkText,
  linkUrl,
  trendPeriod,
  showTrend = false
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200'
  };

  // Calculate trend percentage only if showTrend is true
  let trendPercentage = null;
  if (showTrend && previousValue !== undefined && previousValue !== null && previousValue !== 0) {
    trendPercentage = Math.round(((value - previousValue) / previousValue) * 100);
  }

  return (
    <div className={`p-6 rounded-lg border ${colorClasses[color]} bg-white shadow-sm hover:shadow-md transition-shadow duration-300`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {showTrend && (trendPercentage !== null || trendPeriod) && (
            <p className={`text-sm flex items-center ${trendPercentage > 0 ? 'text-green-600' : trendPercentage < 0 ? 'text-red-600' : 'text-gray-600'}`}>
              {trendPercentage !== null && (
                <>
                  {trendPercentage > 0 ? (
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  ) : trendPercentage < 0 ? (
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  ) : null}
                  <span>{trendPercentage > 0 ? '+' : ''}{trendPercentage}%</span>
                </>
              )}
              {trendPeriod && <span className="ml-1">({trendPeriod})</span>}
            </p>
          )}
        </div>
        {icon && (
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
            </svg>
          </div>
        )}
      </div>
      {linkText && linkUrl && (
        <div className="mt-4">
          <a href={linkUrl} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center">
            {linkText}
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      )}
    </div>
  );
};

// Default export for backward compatibility
const DashboardCharts = {
  ActivityChart,
  UserDistributionChart,
  CompanyStatusChart,
  MetricsCard
};

export default DashboardCharts;